import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSharesLoaded, setSharesLoading, setOwnedShares, setReceivedShares, upsertOwnedShare, upsertReceivedShare, removeOwnedShare, removeReceivedShare, clearShares, setListenerUserIds } from '../Redux/store';
import { supabase } from '../supabase/client';
import { saveOwnedSharesToCache, saveReceivedSharesToCache, loadOwnedSharesFromCache, loadReceivedSharesFromCache, clearSharesCache } from '../data/sharesCache';

const TAG = '[CalendarSharesLoader]';

/**
 * CalendarSharesLoader - Loads calendar shares once and listens for real-time updates
 * 
 * Architecture:
 * 1. Load shares: Supabase → Redux (one-time) - separate queries for owned and received
 * 2. Single realtime subscription: listens to all changes where user is owner OR recipient
 * 3. Client-side filtering routes events to appropriate Redux state (owned vs received)
 * 
 * Redux has two separate states: ownedShares and receivedShares
 * 
 * NOTE: We use a single subscription without server-side filters because:
 * - When a recipient deletes a share, the owner needs to see the deletion
 * - When an owner deletes a share, the recipient needs to see the deletion
 * - Server-side filters would prevent the other party from receiving DELETE events
 * - RLS policy ensures we only receive events for rows we're involved in
 */
export default function CalendarSharesLoader() {
  const dispatch = useDispatch();
  const authUserId = useSelector((s) => s.user.authUserId);
  const ownedShares = useSelector((s) => s.calendarShares.ownedShares);
  const receivedShares = useSelector((s) => s.calendarShares.receivedShares);
  const ownedSharesRef = useRef(ownedShares);
  const receivedSharesRef = useRef(receivedShares);

  // Sync shares to refs on every render to avoid closure trap
  useEffect(() => {
    ownedSharesRef.current = ownedShares;
  }, [ownedShares]);

  useEffect(() => {
    receivedSharesRef.current = receivedShares;
  }, [receivedShares]);

  // Load shares once: localStorage → Redux → Supabase → Redux → localStorage
  useEffect(() => {
    if (!authUserId) return;

    const loadShares = async () => {
      dispatch(setSharesLoading(true));

      // 1. Load from localStorage first for instant display
      const cachedOwned = loadOwnedSharesFromCache();
      const cachedReceived = loadReceivedSharesFromCache();
      
      if (cachedOwned) {
        const ownedArray = Object.values(cachedOwned);
        dispatch(setOwnedShares(ownedArray));
      }
      
      if (cachedReceived) {
        const receivedArray = Object.values(cachedReceived);
        dispatch(setReceivedShares(receivedArray));
      }

      // 2. Load owned shares from Supabase (where user is owner)
      const { data: ownedData, error: ownedError } = await supabase
        .from('calendar_shares')
        .select('*')
        .eq('owner_id', authUserId);

      if (ownedError) {
        console.error(`${TAG} Error loading owned shares:`, ownedError);
      }

      // 3. Load received shares from Supabase (where user is recipient)
      const { data: receivedData, error: receivedError } = await supabase
        .from('calendar_shares')
        .select('*')
        .eq('shared_with_id', authUserId);

      if (receivedError) {
        console.error(`${TAG} Error loading received shares:`, receivedError);
      }

      if (ownedData) {
        dispatch(setOwnedShares(ownedData));
        // Save to localStorage
        const ownedMap = {};
        ownedData.forEach((share) => {
          ownedMap[share.id] = share;
        });
        saveOwnedSharesToCache(ownedMap);
      }

      if (receivedData) {
        dispatch(setReceivedShares(receivedData));
        // Save to localStorage
        const receivedMap = {};
        receivedData.forEach((share) => {
          receivedMap[share.id] = share;
        });
        saveReceivedSharesToCache(receivedMap);
      }

      dispatch(setSharesLoaded(true));
      dispatch(setSharesLoading(false));
    };

    loadShares();
  }, [authUserId]); // Only run when authUserId changes

  // Realtime: Listen to all changes where user is involved (owner or recipient)
  useEffect(() => {
    if (!authUserId) return;

    // Helper logic to process realtime payloads
    const handlePayload = (payload) => {
      const share = payload.eventType === 'DELETE' ? payload.old : payload.new;
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        // Determine if this share belongs to owned or received state
        const isOwned = share.owner_id === authUserId;
        const isReceived = share.shared_with_id === authUserId;

        if (isOwned) {
          dispatch(upsertOwnedShare(payload.new));
          // Update localStorage cache
          const currentOwned = ownedSharesRef.current || {};
          saveOwnedSharesToCache({ 
            ...currentOwned, 
            [payload.new.id]: payload.new
          });
        }
        if (isReceived) {
          dispatch(upsertReceivedShare(payload.new));
          // Update localStorage cache
          const currentReceived = receivedSharesRef.current || {};
          saveReceivedSharesToCache({ 
            ...currentReceived,
            [payload.new.id]: payload.new
          });
        }
      } else if (payload.eventType === 'DELETE') {
        // On DELETE, Supabase only returns the ID, so we check which state contains it
        const currentOwned = ownedSharesRef.current || {};
        const currentReceived = receivedSharesRef.current || {};
        
        const existsInOwned = currentOwned[share.id];
        const existsInReceived = currentReceived[share.id];

        if (existsInOwned) {
          dispatch(removeOwnedShare({ id: share.id }));
          // Update localStorage cache
          const updatedOwned = { ...currentOwned };
          delete updatedOwned[share.id];
          saveOwnedSharesToCache(updatedOwned);
        }
        if (existsInReceived) {
          dispatch(removeReceivedShare({ id: share.id }));
          // Update localStorage cache
          const updatedReceived = { ...currentReceived };
          delete updatedReceived[share.id];
          saveReceivedSharesToCache(updatedReceived);
        }
      }
    };

    // Single channel: Listen to all changes where user is owner OR recipient
    // RLS policy ensures we only receive events for rows we're involved in
    const channel = supabase
      .channel(`calendar_shares:${authUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calendar_shares',
        },
        handlePayload
      )
      .subscribe((status) => {
        // Subscription status changed
      });

    // Cleanup: Remove channel on unmount or auth swap
    return () => {
      supabase.removeChannel(channel);
    };
  }, [authUserId, dispatch]);

  // Clear shares on logout
  useEffect(() => {
    if (!authUserId) {
      dispatch(clearShares());
      clearSharesCache();
    }
  }, [authUserId, dispatch]);

  // Extract user IDs from shares and set listenerUserIds
  useEffect(() => {
    if (!ownedShares || !receivedShares) return;

    // Extract user IDs from owned shares (shared_with_id - IPs the donor is sharing with)
    const ownedUserIds = Object.values(ownedShares).map(share => share.shared_with_id);

    // Extract user IDs from received shares (owner_id - IPs who shared with the donor)
    const receivedUserIds = Object.values(receivedShares).map(share => share.owner_id);

    // Combine and deduplicate
    const allUserIds = [...new Set([...ownedUserIds, ...receivedUserIds])];

    dispatch(setListenerUserIds(allUserIds));
  }, [ownedShares, receivedShares, dispatch]);

  return null;
}
